import { createContext, use, type ReactNode } from 'react';
import type { DisclaimersResolveDisclaimerData } from '@givmo/api-client';
import type { BoardOperations } from './board-operations';
import { ToDoStoreProvider } from './features/todo/todo-store';
import type { BoardConnectionOperations } from './calendar/connection-operations';
import type { BoardDirectorOperations } from './director/director-operations';

/**
 * The host's own words for the organization. The module names no class: the console that mounts it
 * supplies the noun its copy uses, so one build reads right in every console that hosts it.
 */
export interface BoardHostVocab {
  /** What this host calls the organization in running copy. */
  orgNoun: string;
}

/**
 * The disclaimer registry's resolve target for one organization, in the key its class is addressed by
 * there. The registry's own generated query type, minus the surface the component names; the module
 * never spells one of its fields.
 */
export type BoardDisclaimerTarget = Omit<
  NonNullable<DisclaimersResolveDisclaimerData['query']>,
  'surface'
>;

/**
 * The console's active organization and the person's effective permissions there, as the host's own
 * standing read has them (CBG-AI5-PROPOSE). The console's menu draws OUTSIDE Board's officer host
 * (`BoardOfficerProvider` wraps Board's pages only), so the two counts Board supplies to it (Org
 * Machinery OM-30's `countBadge`) read this instead, the host's own standing read.
 */
export interface BoardStanding {
  orgRef: string | null;
  permissions: readonly string[] | null;
}

export interface BoardRuntime {
  operations: BoardOperations;
  /**
   * The director's port: the organization door's director operations (CBG-SEC2). Both class consoles
   * pass `createOrgBoardDirectorOperations()`; a director surface reaches only this, and an officer
   * surface only `operations` (`director/capacity-rail.test.ts`).
   */
  directorOperations: BoardDirectorOperations;
  /**
   * The organization's connected accounts: core's `/org/{org_ref}/integrations` (CBG-CAL1). Like the
   * director family it has no class door, so every host passes `createOrgConnectionOperations()`.
   * Only an officer surface calls it (`director/capacity-rail.test.ts`).
   */
  connectionOperations: BoardConnectionOperations;
  /** Empty only in a test host. Both class consoles mount the same module at /board. */
  basePath: string;
  vocab: BoardHostVocab;
  /** How this host's class addresses the disclaimer registry for one organization; null for none. */
  disclaimerTarget: (orgRef: string) => BoardDisclaimerTarget | null;
  /**
   * CBG-YR4-PAY: whether this host's organizations answer Form 990's Part VI. The compensation
   * decisions ask for, and show, the organization's line-15 answer only where they do: the backend
   * takes one for that one class alone (the contract's §10), and no read says which class it is. A
   * host that says nothing is offered no line.
   */
  answersForm990?: boolean;
  /**
   * CBG-AI5-PROPOSE: a HOOK over the host's standing read, for the menu's two counts (proposed
   * members waiting; batches of past records waiting). The same function for the app's whole life,
   * so the rules of hooks hold. A host that supplies none shows no count.
   */
  useStanding?: () => BoardStanding;
}

const RuntimeContext = createContext<BoardRuntime | null>(null);

export function BoardRuntimeProvider({
  operations,
  directorOperations,
  connectionOperations,
  basePath = '',
  vocab,
  disclaimerTarget,
  answersForm990 = false,
  useStanding,
  children,
}: Omit<BoardRuntime, 'basePath'> & { basePath?: string; children: ReactNode }) {
  return (
    <RuntimeContext
      value={{
        operations,
        directorOperations,
        connectionOperations,
        basePath,
        vocab,
        disclaimerTarget,
        answersForm990,
        ...(useStanding === undefined ? {} : { useStanding }),
      }}
    >
      {/* CBG-UX7: the to-do lists' one store, for as long as the module is mounted. */}
      <ToDoStoreProvider>{children}</ToDoStoreProvider>
    </RuntimeContext>
  );
}

export function useBoardOperations(): BoardOperations {
  const runtime = use(RuntimeContext);
  if (runtime === null) throw new Error('Board operations must be supplied by the host.');
  return runtime.operations;
}

/** The director's port. Only a director surface calls this (`director/capacity-rail.test.ts`). */
export function useBoardDirectorOperations(): BoardDirectorOperations {
  const runtime = use(RuntimeContext);
  if (runtime === null) throw new Error('Board director operations must be supplied by the host.');
  return runtime.directorOperations;
}

/** The connection port (core's connected accounts). Only an officer surface calls this. */
export function useBoardConnectionOperations(): BoardConnectionOperations {
  const runtime = use(RuntimeContext);
  if (runtime === null)
    throw new Error('Board connection operations must be supplied by the host.');
  return runtime.connectionOperations;
}

/**
 * The connection port where a host page may render without Board's runtime (the console's own
 * Settings page): null there, and a caller then reads nothing rather than fail the page.
 */
export function useOptionalBoardConnectionOperations(): BoardConnectionOperations | null {
  return use(RuntimeContext)?.connectionOperations ?? null;
}

/** Whether Board's runtime is mounted above (a host page decides whether to mount a Board card). */
export function useHasBoardRuntime(): boolean {
  return use(RuntimeContext) !== null;
}

export function useBoardBasePath(): string {
  return use(RuntimeContext)?.basePath ?? '';
}

export function useBoardVocab(): BoardHostVocab {
  const runtime = use(RuntimeContext);
  if (runtime === null) throw new Error('Board’s words must be supplied by the host.');
  return runtime.vocab;
}

const NO_STANDING: BoardStanding = { orgRef: null, permissions: null };

function useNoStanding(): BoardStanding {
  return NO_STANDING;
}

/**
 * The console's standing as the host reads it (CBG-AI5-PROPOSE), for a surface outside Board's
 * officer host: none outside a host, or from a host that supplies no reader.
 */
export function useBoardStanding(): BoardStanding {
  const useStanding = use(RuntimeContext)?.useStanding ?? useNoStanding;
  return useStanding();
}

/** Whether the host's organizations answer Form 990's Part VI (CBG-YR4-PAY): false outside a host. */
export function useBoardAnswersForm990(): boolean {
  return use(RuntimeContext)?.answersForm990 === true;
}

/**
 * The registry target for `orgRef`, from the host. Null with no organization, and outside a host:
 * the registry then resolves the bare surface.
 */
export function useBoardDisclaimerTarget(orgRef: string | null): BoardDisclaimerTarget | null {
  const runtime = use(RuntimeContext);
  if (orgRef === null || runtime === null) return null;
  return runtime.disclaimerTarget(orgRef);
}
